
import { HttpBackend, HttpClient, HttpHeaders } from '@angular/common/http';
import { Component, DoCheck, HostBinding, forwardRef, Input, OnDestroy, OnInit, inject } from '@angular/core';
import {
  ControlValueAccessor,
  FormBuilder,
  FormControl,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Subject, of } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  finalize,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { IconDef } from '../icons/icon';
import { InputReusable } from '../input-reusable/input-reusable';
import { SelectComponent } from '../select/select';

interface CraftyAddressResponse {
  error_code?: string;
  town?: string;
  postal_town?: string;
  postal_county?: string;
  traditional_county?: string;
  country?: string;
  delivery_points?: Array<{
    line_1?: string;
    line_2?: string;
  }>;
}

@Component({
  selector: 'app-postcode-reusable',
  standalone: true,
  imports: [ReactiveFormsModule, InputReusable, SelectComponent],
  templateUrl: './postcode-reusable.html',
  styleUrl: './postcode-reusable.css',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PostcodeReusable),
      multi: true,
    },
  ],
})
export class PostcodeReusable implements OnInit, OnDestroy, DoCheck, ControlValueAccessor {
  @HostBinding('class.inline-layout') get inlineLayoutClass(): boolean {
    return this.inlineLayout;
  }

  @Input() controlOnly = false;
  @Input() size: 'sm' | 'md' | 'lg' = 'lg';
  @Input() label = 'Post Code';
  @Input() placeholder = 'e.g. TW89DW';
  @Input() required = false;
  @Input() externalErrorMessage = '';
  @Input() addressLabel = 'Address';
  @Input() addressPlaceholder = 'Address';
  @Input() cityLabel = 'City';
  @Input() cityPlaceholder = 'City';
  @Input() countryLabel = 'Country';
  @Input() countryPlaceholder = 'Country';
  @Input() postcodeControl: FormControl<string | number | null> | null = null;
  @Input() addressControl: FormControl<string | number | null> | null = null;
  @Input() cityControl: FormControl<string | number | null> | null = null;
  @Input() countryControl: FormControl<string | number | null> | null = null;
  @Input() countryOptions: Array<{ label: string; value: string } | string> = [];
  @Input() useAddressDropdown = true;
  @Input() enableLookup = true;
  @Input() showLookupFeedback = true;
  @Input() showPostcodeField = true;
  @Input() showAddressField = true;
  @Input() showCityField = true;
  @Input() showCountryField = true;
  @Input() inlineLayout = false;

  @Input()
  set sameAsCompanyAddress(value: boolean) {
    this._sameAsCompanyAddress = value === true;
    this.form.get('sameAsCompanyAddress')?.setValue(this._sameAsCompanyAddress, { emitEvent: false });
  }
  get sameAsCompanyAddress(): boolean {
    return this._sameAsCompanyAddress;
  }

  private readonly fb = inject(FormBuilder);
  private readonly backend = inject(HttpBackend);
  private readonly rawHttp = new HttpClient(this.backend);
  private _sameAsCompanyAddress = false;

  readonly form = this.fb.group({
    sameAsCompanyAddress: [false],
    postcode: ['', [Validators.required]],
    address: ['', [Validators.required]],
    city: ['', [Validators.required]],
    country: ['United Kingdom', [Validators.required]],
  });

  readonly externalPostcodeControl = new FormControl<string | number | null>('');

  availableAddresses: string[] = [];
  isLoading = false;
  lookupError = '';

  private readonly destroy$ = new Subject<void>();
  private readonly boundExternalControls = new WeakSet<FormControl<string | number | null>>();
  private onChange: (value: string) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  ngOnInit(): void {
    this.setupPostcodeLookup();
    this.setupExternalPostcodeControl();
    this.setupLegacyExternalBindings();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngDoCheck(): void {
    this.setupLegacyExternalBindings();
    this.syncExternalValuesFromInputs();
  }

  writeValue(value: unknown): void {
    this.externalPostcodeControl.setValue(String(value ?? ''), { emitEvent: false });
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.externalPostcodeControl.disable({ emitEvent: false });
      return;
    }

    this.externalPostcodeControl.enable({ emitEvent: false });
  }

  markAsTouched(): void {
    this.onTouched();
  }

  get fieldGridClass(): string {
    return this.visibleFieldCount <= 1
      ? 'grid grid-cols-1 gap-4'
      : 'grid grid-cols-1 gap-4 lg:grid-cols-2 lg:gap-5';
  }

  private get visibleFieldCount(): number {
    return [
      this.showPostcodeField,
      this.showAddressField,
      this.showCityField,
      this.showCountryField,
    ].filter(Boolean).length;
  }

  private setupExternalPostcodeControl(): void {
    this.externalPostcodeControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      this.onChange(String(value ?? ''));
    });
  }

  private setupLegacyExternalBindings(): void {
    this.bindExternalControl('postcode', this.postcodeControl);
    this.bindExternalControl('address', this.addressControl);
    this.bindExternalControl('city', this.cityControl);
    this.bindExternalControl('country', this.countryControl);
  }

  private bindExternalControl(
    internalControlName: 'postcode' | 'address' | 'city' | 'country',
    externalControl: FormControl<string | number | null> | null,
  ): void {
    if (!externalControl) {
      return;
    }

    const internalControl = this.form.get(internalControlName) as FormControl<string | number | null> | null;
    if (!internalControl) {
      return;
    }

    const externalValue = externalControl.value;
    if (externalValue !== null && externalValue !== undefined && externalValue !== '') {
      internalControl.setValue(externalValue, { emitEvent: false });
    } else {
      externalControl.setValue(internalControl.value, { emitEvent: false });
    }

    if (!this.boundExternalControls.has(externalControl)) {
      externalControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
        if (internalControl.value !== value) {
          internalControl.setValue(value, {
            emitEvent: internalControlName === 'postcode' && this.enableLookup,
          });
        }
      });

      internalControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
        if (externalControl.value !== value) {
          externalControl.setValue(value);
        }
      });

      this.boundExternalControls.add(externalControl);
    }
  }

  private syncExternalValuesFromInputs(): void {
    this.syncExternalValueToInternal('postcode', this.postcodeControl);
    this.syncExternalValueToInternal('address', this.addressControl);
    this.syncExternalValueToInternal('city', this.cityControl);
    this.syncExternalValueToInternal('country', this.countryControl);
  }

  private syncExternalValueToInternal(
    internalControlName: 'postcode' | 'address' | 'city' | 'country',
    externalControl: FormControl<string | number | null> | null,
  ): void {
    if (!externalControl) {
      return;
    }

    const internalControl = this.form.get(internalControlName) as FormControl<string | number | null> | null;
    if (!internalControl) {
      return;
    }

    if (internalControl.value !== externalControl.value) {
      internalControl.setValue(externalControl.value, {
        emitEvent: internalControlName === 'postcode' && this.enableLookup,
      });
    }
  }

  private setupPostcodeLookup(): void {
    if (!this.enableLookup) {
      return;
    }

    const postcodeControl = this.form.get('postcode');
    if (!postcodeControl) {
      return;
    }

    postcodeControl.valueChanges
      .pipe(
        debounceTime(500),
        distinctUntilChanged(),
        tap(() => {
          this.lookupError = '';
        }),
        switchMap((rawValue) => {
          const postcode = String(rawValue || '')
            .trim()
            .toUpperCase();
          const stripped = postcode.replace(/\s/g, '');
          const sameAsCompanyAddress = this.form.get('sameAsCompanyAddress')?.value === true;

          // If the postcode is invalid or empty, clear address options.
          if (!stripped || stripped.length < 5 || postcodeControl.invalid) {
            this.clearAddressOptions();
            this.form.patchValue({ city: '' });
            return of(null);
          }

          // If the form is marked as 'same as company address' we should not
          // perform a postcode lookup nor clear any existing address value
          // (the external form will already have the company address).
          if (sameAsCompanyAddress) {
            return of(null);
          }

          this.isLoading = true;
          return this.lookupAddressByPostcode(postcode).pipe(
            finalize(() => {
              this.isLoading = false;
            }),
          );
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((response) => {
        if (!response) {
          return;
        }

        if (response.error_code) {
          this.clearAddressOptions();
          this.form.patchValue({ city: '' });
          this.lookupError = 'No addresses found for this postcode.';
          return;
        }

        const city = response.town || response.postal_town || '';
        const county = response.postal_county || response.traditional_county || '';
        const country = this.normalizeCountry(response.country || 'United Kingdom');
        const deliveryPoints = Array.isArray(response.delivery_points)
          ? response.delivery_points
          : [];

        const formatted = deliveryPoints
          .map((item) => {
            const line1 = item?.line_1 || '';
            const line2 = item?.line_2 || '';
            return [line1, line2, city, county].filter(Boolean).join(', ');
          })
          .filter((address) => !!address);

        const uniqueAddresses = [...new Set(formatted)];
        this.availableAddresses = uniqueAddresses;

        const patch: Record<string, string> = {};

        if (uniqueAddresses.length === 1) {
          patch['address'] = uniqueAddresses[0];
        } else {
          patch['address'] = '';
        }

        if (city) {
          patch['city'] = city;
        } else {
          patch['city'] = '';
        }

        if (country) {
          patch['country'] = country;
        }

        if (Object.keys(patch).length > 0) {
          this.form.patchValue(patch);
        }
      });
  }

  private normalizeCountry(country: string): string {
    const value = String(country || '')
      .trim()
      .toLowerCase();
    if (
      value === 'england' ||
      value === 'scotland' ||
      value === 'wales' ||
      value === 'northern ireland'
    ) {
      return 'United Kingdom';
    }
    return country || 'United Kingdom';
  }

  private formatUKPostcode(raw: string): string {
    const clean = raw.replace(/\s/g, '').toUpperCase();
    if (clean.length >= 5 && clean.length <= 7) {
      return `${clean.slice(0, -3)} ${clean.slice(-3)}`;
    }
    return clean;
  }

  private lookupAddressByPostcode(postcode: string) {
    const formatted = this.formatUKPostcode(postcode);
    const payload = {
      key: '3d776-8d063-700fe-07aa9',
      postcode: formatted,
      response: 'data_formatted',
    };

    return this.rawHttp
      .post<CraftyAddressResponse>('https://pcls1.craftyclicks.co.uk/json/rapidaddress', payload, {
        headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
      })
      .pipe(
        catchError(() => {
          this.lookupError = 'Unable to fetch addresses right now.';
          this.clearAddressOptions();
          return of({ error_code: 'request_failed' } as CraftyAddressResponse);
        }),
      );
  }

  private clearAddressOptions(): void {
    this.availableAddresses = [];
    this.isLoading = false;
    this.form.patchValue({ address: '' });
  }
}
